import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@ouropix/ui';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { loginWithEmail, type Verify2FAResponse } from '@/lib/auth';
import type { AdminSession } from '@/stores/auth';
import { StepUpModal } from '@/components/auth/StepUpModal';
import { GPSBadge } from '@/components/auth/GPSBadge';
import { ForgotPasswordFlow } from '@/components/auth/ForgotPasswordFlow';
import { Admin2FAVerifyModal } from '@/components/admin/Admin2FAVerifyModal';
import { useAdminGeolocation } from '@/hooks/useAdminGeolocation';
import { useAdminForgotPassword } from '@/hooks/useAdminForgotPassword';
import type { ForgotPasswordGps } from '@/lib/auth';
import { api } from '@/lib/api';

const USE_COOKIES = import.meta.env.VITE_ADMIN_AUTH_COOKIES === 'true';
const DEV_MODE = import.meta.env.VITE_ADMIN_DEV_MODE === 'true';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  beforeLoad: async () => {
    const state = useAuthStore.getState();
    const session = state.session;
    if (!session) return;
    if (USE_COOKIES) {
      // Valida cookie no servidor antes de redirecionar pra /dashboard.
      // Sem isso, localStorage "fantasma" causa loop /login <-> /dashboard.
      try {
        await api.get('/auth/admin/me');
        throw redirect({ to: '/dashboard' });
      } catch (err: unknown) {
        // re-throw redirect (TanStack Router exception); demais erros = cookie invalido.
        if (err && typeof err === 'object' && 'to' in (err as Record<string, unknown>)) {
          throw err;
        }
        state.logout();
        return;
      }
    }
    if (session.tokenExpiresAt && session.tokenExpiresAt > Date.now()) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const startTwoFactorStep = useAuthStore((s) => s.startTwoFactorStep);
  const requires2FA = useAuthStore((s) => s.requires2FA);
  const pendingUserId = useAuthStore((s) => s.pendingUserId);
  const clearTwoFactorStep = useAuthStore((s) => s.clearTwoFactorStep);

  const geo = useAdminGeolocation();
  const gpsAvailable = !geo.denied && !geo.loading && geo.lat !== null;

  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const canSubmit = !loginLoading && (gpsAvailable || geo.gpsTimedOut || DEV_MODE);

  const snapshotGps = (): ForgotPasswordGps => ({
    lat: Number(geo.lat ?? 0),
    lon: Number(geo.lon ?? 0),
    accuracy: geo.accuracy ?? null,
  });

  const forgotFlow = useAdminForgotPassword({
    gps: snapshotGps,
    canSubmit,
    onSuccess: () => setMode('login'),
  });

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoginLoading(true);

    try {
      const gpsBypassed = (DEV_MODE || geo.gpsTimedOut) && !gpsAvailable;
      const result = await loginWithEmail({
        email: email.trim().toLowerCase(),
        password,
        gps: gpsAvailable
          ? { lat: Number(geo.lat), lon: Number(geo.lon), accuracy: geo.accuracy ?? 0 }
          : gpsBypassed
            ? { lat: 0, lon: 0, accuracy: 0 }
            : undefined,
        gpsBypassed,
      });

      if (!result.success) {
        toast.error(result.message || 'Email ou senha incorretos.');
        return;
      }

      if (result.requiresMfaSetup && result.userId && result.tempToken) {
        toast.info('Sua conta requer configuração de 2FA.');
        navigate({
          to: '/setup-2fa',
          search: { userId: result.userId, tempToken: result.tempToken },
        });
        return;
      }

      if (result.requires2FA && result.userId && result.tempToken) {
        startTwoFactorStep({ userId: result.userId, tempToken: result.tempToken });
        return;
      }

      if (USE_COOKIES ? result.success : result.token) {
        const session: AdminSession = {
          userId: result.userId || '',
          email: result.email || '',
          fullName: result.fullName || '',
          role: result.role || 'moderator',
          twoFactorEnabled: result.twoFactorEnabled ?? false,
          ...(USE_COOKIES
            ? {}
            : {
                token: result.token,
                refreshToken: result.refreshToken || '',
                tokenExpiresAt: Date.now() + (result.expiresIn ?? 7 * 24 * 3600) * 1000,
              }),
        };
        setSession(session);
        toast.success('Login realizado com sucesso!');
        navigate({ to: '/dashboard' });
      }
    } catch {
      toast.error('Falha ao autenticar. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  }

  const handle2FAVerified = (result: Verify2FAResponse) => {
    const session: AdminSession = {
      userId: pendingUserId || '',
      email: result.email || '',
      fullName: result.fullName || '',
      role: result.role || 'moderator',
      twoFactorEnabled: true,
      ...(result.token
        ? {
            token: result.token,
            refreshToken: result.refreshToken || '',
            tokenExpiresAt: Date.now() + (result.expiresIn ?? 7 * 24 * 3600) * 1000,
          }
        : {}),
    };
    setSession(session);
    toast.success('Login realizado com sucesso!');
    navigate({ to: '/dashboard' });
  };

  const handle2FACancel = () => {
    clearTwoFactorStep();
  };

  const backToLogin = () => {
    forgotFlow.reset();
    setMode('login');
  };

  const openForgotFlow = () => {
    forgotFlow.setEmail(email);
    forgotFlow.setStep('email');
    setMode('forgot');
  };

  const renderLogin = () => (
    <>
      <CardHeader className="space-y-1 text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl font-bold">Painel Administrativo</CardTitle>
        <CardDescription>
          Faça login com suas credenciais de administrador
        </CardDescription>
      </CardHeader>

      <CardContent>
        <GPSBadge geo={geo} gpsAvailable={gpsAvailable} devMode={DEV_MODE} />

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="admin@sistema.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={loginLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={loginLoading}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                tabIndex={-1}
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>

          <Button type="submit" className="w-full btn-shine-effect" disabled={!canSubmit}>
            {loginLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Entrando...
              </>
            ) : (
              'Entrar no Painel Admin'
            )}
          </Button>
        </form>

        <div className="mt-4 flex flex-col items-center gap-2">
          <Button variant="link" onClick={openForgotFlow} className="text-sm">
            Esqueci minha senha
          </Button>
          <Button variant="link" asChild className="text-sm">
            <a
              href={import.meta.env.VITE_SITE_URL ?? 'http://localhost:3001'}
              rel="noopener noreferrer"
            >
              Voltar para o site
            </a>
          </Button>
        </div>
      </CardContent>
    </>
  );

  return (
    <>
      <StepUpModal />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md card-shadow">
          {mode === 'login' ? (
            renderLogin()
          ) : (
            <ForgotPasswordFlow
              flow={forgotFlow}
              canSubmit={canSubmit}
              gpsBadge={<GPSBadge geo={geo} gpsAvailable={gpsAvailable} />}
              onBackToLogin={backToLogin}
            />
          )}
        </Card>
      </div>

      <Admin2FAVerifyModal
        open={requires2FA}
        onVerified={handle2FAVerified}
        onCancel={handle2FACancel}
      />
    </>
  );
}
